/**
 * Margin leak (card af640ca0): the jobs columns that carry the client's price or the
 * commission rate, and the column list shared queries use instead of `jobs(*)`.
 *
 * Migration B (20261201300000_client_money_column_boundary.sql) withholds JOB_CLIENT_MONEY_COLUMNS
 * and its counterparts below from `authenticated` and `anon`: SELECT, INSERT and UPDATE. A
 * `select('*')` or a `jobs(*)` embed fails with 42501 for every signed-in role, so shared code that
 * needs "the whole job" must name its columns. Trades never read these columns; platform admins and
 * the owning client read them through the `job_client_pricing` view.
 */

import type { Database } from "../database.types";

type JobsRow = Database["public"]["Tables"]["jobs"]["Row"];

/** Client price and commission-rate columns on jobs. No trade-facing query may select them. */
export const JOB_CLIENT_MONEY_COLUMNS = [
  "estimated_value",
  "final_value",
  "budget_cents",
  "gst_amount_cents",
  "client_total_cents",
  "commission_rate_bps",
  "commission_override_bps",
  "commission_rate_source",
  "line_items",
  "terms_fee_bps",
  "terms_fee_cents",
  "terms_fee_gst_cents",
] as const satisfies readonly (keyof JobsRow)[];

export type JobClientMoneyColumn = (typeof JOB_CLIENT_MONEY_COLUMNS)[number];

/**
 * Homeowner contact and exact site details on jobs (leak closed in PR C, PR #3455).
 * Since PR C Phase 2 no signed-in role can SELECT them on jobs at all (the table-level
 * grant is replaced by a column grant that leaves these out), so a query that names one,
 * filters on one or uses `*` fails with 42501. Parties read them through
 * job_private_details or company_jobs_dashboard. site_unit (the unit number of
 * site_address, added by the partner API) is withheld with them; no view exposes it yet
 * because nothing displays it.
 */
export const JOB_PRIVATE_COLUMNS = [
  "site_address",
  "site_access_instructions",
  "site_latitude",
  "site_longitude",
  "end_customer_name",
  "end_customer_email",
  "end_customer_phone",
  "end_customer_address",
  "end_customer_suburb",
  "end_customer_state",
  "end_customer_postcode",
  "site_unit",
] as const satisfies readonly (keyof JobsRow)[];

export type JobPrivateColumn = (typeof JOB_PRIVATE_COLUMNS)[number];

/**
 * Every other jobs column, for embeds that used `*` (workflow step conditions can test any
 * job field, so the list stays complete).
 */
export const TRADE_SAFE_JOB_COLUMNS = [
  "id", "customer_id", "assigned_trade_id", "parent_job_id", "specific_job_type_id", "title", "description",
  "site_address", "site_suburb", "site_state", "site_postcode", "preferred_start_date",
  "preferred_completion_date", "urgency_level", "job_status", "creation_pathway", "requires_site_visit",
  "special_requirements", "photos_urls", "internal_notes", "created_at", "updated_at", "completed_at",
  "booking_hash", "site_latitude", "site_longitude", "payment_intent_id", "payment_status",
  "scheduled_date", "payout_status", "admin_approved_at", "admin_approved_by", "estimated_hours",
  "accepted_quote_id", "quote_lock_version", "external_signoff_reference", "external_signoff_at",
  "signoff_evidence_url", "preferred_start_time", "trade_net_cents", "preauth_scheduled_at",
  "preauth_triggered", "cancelled_at", "cancellation_reason", "cancelled_by", "external_reference",
  "is_sandbox", "trade_gst_amount_cents", "job_origin", "direct_customer_id", "direct_quote_id",
  "schedule_confirmed_at", "schedule_confirmed_by", "date_range_start", "date_range_end",
  "payment_capture_date", "last_confirmed_at", "site_access_instructions", "requires_approval",
  "schedule_confirmed", "work_started_at", "cancelled_by_role", "scheduled_time_slot",
  "scheduled_window_start", "scheduled_window_end", "is_strict_schedule_window", "is_paused",
  "admin_pause_reason", "paused_at", "paused_by", "ai_generated_description", "ai_description_status",
  "ai_description_generated_at", "ai_description_error", "ai_description_source_doc_ids",
  "ai_description_source_photo_urls", "view_count", "is_warranty_job", "warranty_claim_id",
  "skip_payment_processing", "requires_on_site_payment", "end_customer_name", "end_customer_email",
  "end_customer_phone", "end_customer_balance_cents", "end_customer_payment_intent_id",
  "end_customer_payment_status", "end_customer_payment_method", "end_customer_paid_at",
  "on_site_payment_failure_reason", "on_site_payment_evidence_path", "end_customer_address",
  "end_customer_suburb", "end_customer_state", "end_customer_postcode", "on_site_payment_attempts",
  "id_text", "auto_paused_no_billing", "parts_warranty_months", "labour_warranty_months", "job_role",
  "subjob_seq", "subjob_label", "subjob_progress", "uses_payment_phases", "end_customer_authorized_at",
  "assignment_mode", "external_workflow_links", "inventory_managed", "requires_hardware",
  "bom_accepted_by_trade_at", "bom_accepted_by_trade_id", "retailer_supplied_equipment",
  "trade_supplied_equipment", "logistics_mode", "logistics_notes", "ai_description_started_at",
  "work_completed_at", "billing_type", "search_buffer_km", "offer_expires_at", "offer_reminder_sent_at",
  "trade_sla_warning_24h_sent_at", "trade_sla_warning_4h_sent_at", "trade_sla_warning_1h_sent_at",
  "trade_sla_forfeited_at", "trade_sla_extended_until", "assigned_at", "trade_sla_forfeiture_mode",
  "original_client_description", "client_description_confirmed_at", "rate_card_id",
  "trade_settled_amount_cents",
] as const satisfies readonly Exclude<keyof JobsRow, JobClientMoneyColumn>[];

export type TradeSafeJobRow = Pick<JobsRow, (typeof TRADE_SAFE_JOB_COLUMNS)[number]>;

/** TRADE_SAFE_JOB_COLUMNS as a PostgREST column list, e.g. `job:jobs ( ${TRADE_SAFE_JOB_SELECT} )`. */
export const TRADE_SAFE_JOB_SELECT: string = TRADE_SAFE_JOB_COLUMNS.join(", ");

/**
 * Public jobs columns: TRADE_SAFE_JOB_COLUMNS minus JOB_PRIVATE_COLUMNS and internal_notes.
 * Used for embeds by trades or non-parties who may not read homeowner contact details or exact site coordinates.
 * internal_notes is not money, so Migration B leaves it readable: builds up to 1.1.68 name it in this list. No
 * workflow template tests it and no screen shows it, so it is left out here and can be revoked once those
 * builds are below the forced-update minimum.
 */
export const TRADE_SAFE_JOB_PUBLIC_COLUMNS = [
  "id", "customer_id", "assigned_trade_id", "parent_job_id", "specific_job_type_id", "title", "description",
  "site_suburb", "site_state", "site_postcode", "preferred_start_date",
  "preferred_completion_date", "urgency_level", "job_status", "creation_pathway", "requires_site_visit",
  "special_requirements", "photos_urls", "created_at", "updated_at", "completed_at",
  "booking_hash", "payment_intent_id", "payment_status",
  "scheduled_date", "payout_status", "admin_approved_at", "admin_approved_by", "estimated_hours",
  "accepted_quote_id", "quote_lock_version", "external_signoff_reference", "external_signoff_at",
  "signoff_evidence_url", "preferred_start_time", "trade_net_cents", "preauth_scheduled_at",
  "preauth_triggered", "cancelled_at", "cancellation_reason", "cancelled_by", "external_reference",
  "is_sandbox", "trade_gst_amount_cents", "job_origin", "direct_customer_id", "direct_quote_id",
  "schedule_confirmed_at", "schedule_confirmed_by", "date_range_start", "date_range_end",
  "payment_capture_date", "last_confirmed_at", "requires_approval",
  "schedule_confirmed", "work_started_at", "cancelled_by_role", "scheduled_time_slot",
  "scheduled_window_start", "scheduled_window_end", "is_strict_schedule_window", "is_paused",
  "admin_pause_reason", "paused_at", "paused_by", "ai_generated_description", "ai_description_status",
  "ai_description_generated_at", "ai_description_error", "ai_description_source_doc_ids",
  "ai_description_source_photo_urls", "view_count", "is_warranty_job", "warranty_claim_id",
  "skip_payment_processing", "requires_on_site_payment",
  "end_customer_balance_cents", "end_customer_payment_intent_id",
  "end_customer_payment_status", "end_customer_payment_method", "end_customer_paid_at",
  "on_site_payment_failure_reason", "on_site_payment_evidence_path",
  "on_site_payment_attempts",
  "id_text", "auto_paused_no_billing", "parts_warranty_months", "labour_warranty_months", "job_role",
  "subjob_seq", "subjob_label", "subjob_progress", "uses_payment_phases", "end_customer_authorized_at",
  "assignment_mode", "external_workflow_links", "inventory_managed", "requires_hardware",
  "bom_accepted_by_trade_at", "bom_accepted_by_trade_id", "retailer_supplied_equipment",
  "trade_supplied_equipment", "logistics_mode", "logistics_notes", "ai_description_started_at",
  "work_completed_at", "billing_type", "search_buffer_km", "offer_expires_at", "offer_reminder_sent_at",
  "trade_sla_warning_24h_sent_at", "trade_sla_warning_4h_sent_at", "trade_sla_warning_1h_sent_at",
  "trade_sla_forfeited_at", "trade_sla_extended_until", "assigned_at", "trade_sla_forfeiture_mode",
  "original_client_description", "client_description_confirmed_at", "rate_card_id",
  "trade_settled_amount_cents",
] as const satisfies readonly Exclude<keyof JobsRow, JobClientMoneyColumn | JobPrivateColumn | "internal_notes">[];

export type TradeSafeJobPublicRow = Pick<JobsRow, (typeof TRADE_SAFE_JOB_PUBLIC_COLUMNS)[number]>;

/** TRADE_SAFE_JOB_PUBLIC_COLUMNS as a PostgREST column list. */
export const TRADE_SAFE_JOB_PUBLIC_SELECT: string = TRADE_SAFE_JOB_PUBLIC_COLUMNS.join(", ");

type QuotesRow = Database["public"]["Tables"]["quotes"]["Row"];
type TaskAssignmentsRow = Database["public"]["Tables"]["job_task_assignments"]["Row"];
type MilestonesRow = Database["public"]["Tables"]["job_payment_milestones"]["Row"];
type DashboardRow = Database["public"]["Views"]["company_jobs_dashboard"]["Row"];

/** Client price and commission-rate columns on quotes. The trade's own figures are trade_*_cents. */
export const QUOTE_CLIENT_MONEY_COLUMNS = [
  "total_amount",
  "subtotal",
  "gst_amount",
  "price_cents",
  "commission_rate",
  "commission_rate_bps",
  "original_job_amount_cents",
] as const satisfies readonly (keyof QuotesRow)[];

export type QuoteClientMoneyColumn = (typeof QUOTE_CLIENT_MONEY_COLUMNS)[number];

/** Every other quotes column: what a trade may read about a quote (its own payout in trade_*_cents). */
export const TRADE_SAFE_QUOTE_COLUMNS = [
  "id", "job_id", "trade_company_id", "quote_number", "status", "valid_until", "notes",
  "terms_and_conditions", "submitted_at", "approved_by", "approved_at", "rejected_by", "rejected_at",
  "expires_at", "rejection_reason", "created_at", "updated_at", "scope_of_work", "inclusions", "exclusions",
  "line_items", "estimated_hours", "team_size", "is_counter_offer", "counter_offer_message", "revision_number",
  "superseded_by_quote_id", "quoter_profile_id", "nominated_staff_profile_ids", "trade_net_cents",
  "trade_gst_cents", "trade_total_cents",
] as const satisfies readonly Exclude<keyof QuotesRow, QuoteClientMoneyColumn>[];

export type TradeSafeQuoteRow = Pick<QuotesRow, (typeof TRADE_SAFE_QUOTE_COLUMNS)[number]>;

/** TRADE_SAFE_QUOTE_COLUMNS as a PostgREST column list. */
export const TRADE_SAFE_QUOTE_SELECT: string = TRADE_SAFE_QUOTE_COLUMNS.join(", ");

/** Client rate columns on job_task_assignments. The trade's own are trade_rate_cents / trade_total_cents. */
export const TASK_ASSIGNMENT_CLIENT_MONEY_COLUMNS = [
  "client_rate_cents",
  "client_total_cents",
] as const satisfies readonly (keyof TaskAssignmentsRow)[];

export type TaskAssignmentClientMoneyColumn = (typeof TASK_ASSIGNMENT_CLIENT_MONEY_COLUMNS)[number];

/** Every other job_task_assignments column. */
export const TRADE_SAFE_TASK_ASSIGNMENT_COLUMNS = [
  "id", "job_id", "job_task_id", "quantity", "unit", "trade_rate_cents", "trade_total_cents", "created_at",
  "updated_at", "custom_name",
] as const satisfies readonly Exclude<keyof TaskAssignmentsRow, TaskAssignmentClientMoneyColumn>[];

/** TRADE_SAFE_TASK_ASSIGNMENT_COLUMNS as a PostgREST column list. */
export const TRADE_SAFE_TASK_ASSIGNMENT_SELECT: string = TRADE_SAFE_TASK_ASSIGNMENT_COLUMNS.join(", ");

/** Client money columns on job_payment_milestones. The trade's own are trade_net_cents / trade_gst_cents. */
export const MILESTONE_CLIENT_MONEY_COLUMNS = [
  "amount_cents",
  "ex_gst_cents",
  "gst_amount_cents",
  "commission_cents",
] as const satisfies readonly (keyof MilestonesRow)[];

/**
 * company_jobs_dashboard columns that carry the client's price (null for a trade), plus
 * service_base_price, an admin-set price per service type. A trade-facing read selects
 * TRADE_SAFE_DASHBOARD_COLUMNS instead.
 */
export const DASHBOARD_CLIENT_MONEY_COLUMNS = [
  "estimated_value",
  "final_value",
  "budget_cents",
  "gst_amount_cents",
  "client_total_cents",
  "subjobs_client_total_cents",
  "subjobs_estimated_value",
  "subjobs_gst_amount_cents",
  "service_base_price",
] as const satisfies readonly (keyof DashboardRow)[];

export type DashboardClientMoneyColumn = (typeof DASHBOARD_CLIENT_MONEY_COLUMNS)[number];

/** Every other company_jobs_dashboard column. */
export const TRADE_SAFE_DASHBOARD_COLUMNS = [
  "id", "title", "description", "status", "job_status", "creation_pathway", "urgency_level", "created_at",
  "updated_at", "completed_at", "customer_id", "customer_name", "customer_abn", "customer_acn",
  "customer_status", "customer_type", "site_address", "site_suburb", "site_state", "site_postcode",
  "site_latitude", "site_longitude", "assigned_trade_id", "trade_company_name", "trade_company_abn",
  "assigned_trade_user_id", "assigned_trade_user_name", "assigned_trade_user_email", "requires_site_visit",
  "trade_net_cents", "trade_gst_amount_cents", "specific_job_type_id", "service_type_name",
  "service_type_description", "scheduled_date", "preferred_start_date", "preferred_start_time",
  "preferred_completion_date", "special_requirements", "photo_count", "feedback_rating", "feedback_text",
  "payment_status", "payout_status", "admin_approved_at", "admin_approved_by", "estimated_hours",
  "accreditations", "required_trades", "quote_count", "external_reference", "job_date",
  "assigned_trade_users", "scheduled_time_slot", "scheduled_window_start", "scheduled_window_end",
  "date_range_start", "date_range_end", "is_warranty_job", "warranty_claim_id", "is_paused",
  "admin_pause_reason", "parent_job_id", "job_role", "is_subjob", "subjob_count", "subjob_progress",
  "subjobs_breakdown",
  "container_status", "id_text", "site_contact_names", "site_contact_phones", "trade_company_phone",
  "assigned_trade_user_phone", "external_workflow_links", "requires_hardware", "is_strict_schedule_window",
  "end_customer_name", "end_customer_email", "end_customer_phone", "end_customer_address",
  "end_customer_suburb", "end_customer_state", "end_customer_postcode", "end_customer_balance_cents",
  "end_customer_payment_status", "end_customer_payment_method", "end_customer_paid_at",
  "site_access_instructions", "billing_type", "offer_expires_at", "requires_on_site_payment",
  "trade_settled_amount_cents",
] as const satisfies readonly Exclude<keyof DashboardRow, DashboardClientMoneyColumn>[];

/** TRADE_SAFE_DASHBOARD_COLUMNS as a PostgREST column list. */
export const TRADE_SAFE_DASHBOARD_SELECT: string = TRADE_SAFE_DASHBOARD_COLUMNS.join(", ");

type CustomersRow = Database["public"]["Tables"]["customers"]["Row"];

/**
 * The client's commission rate and variation markup on customers. Migration B withholds them from
 * signed-in users; platform admins and the customer's own users read them through
 * customer_client_pricing.
 */
export const CUSTOMER_CLIENT_MONEY_COLUMNS = [
  "commission_rate_bps",
  "variation_markup_percentage",
] as const satisfies readonly (keyof CustomersRow)[];

export type CustomerClientMoneyColumn = (typeof CUSTOMER_CLIENT_MONEY_COLUMNS)[number];

/** Every other customers column, for reads that used `select('*')`. */
export const CUSTOMER_NON_MONEY_COLUMNS = [
  "id", "company_name", "trading_name", "abn", "acn", "address", "suburb", "state", "postcode", "phone", "email",
  "status", "company_type", "api_key", "live_at", "solvency", "created_at", "updated_at", "abn_validated",
  "business_address", "business_suburb", "business_state", "business_postcode", "company_size", "industry_type",
  "website_url", "billing_address", "billing_suburb", "billing_state", "billing_postcode", "same_as_business_address",
  "entity_type_code", "gst_registered", "gst_effective_from", "main_business_name", "other_trading_names",
  "abn_last_validated_at", "abn_lookup_id", "stripe_customer_id", "payment_method", "primary_contact_user_id",
  "onboarding_status", "onboarding_completed_at", "integration_method", "widget_config", "api_keys_enabled",
  "company_uuid", "business_structure",
  "trading_as_individual", "individual_trading_name", "job_auto_approval_enabled", "job_approval_required_threshold",
  "variation_approval_threshold", "variation_contact_name", "variation_contact_phone",
  "variation_customer_acceptance_required", "unit_number", "stripe_account_id", "stripe_onboarding_submitted",
  "stripe_charges_enabled", "stripe_payouts_enabled", "on_site_payment_enabled", "rate_basis",
  "stripe_has_default_payment_method", "linked_trade_company_id", "inventory_management_enabled",
  "delivery_prep_hours", "pickup_prep_hours", "default_scheduling_lead_days", "delivery_prep_business_days",
  "pickup_prep_business_days", "logo_storage_path",
] as const satisfies readonly Exclude<keyof CustomersRow, CustomerClientMoneyColumn>[];

export type CustomerNonMoneyRow = Pick<CustomersRow, (typeof CUSTOMER_NON_MONEY_COLUMNS)[number]>;

/** CUSTOMER_NON_MONEY_COLUMNS as a PostgREST column list. */
export const CUSTOMER_NON_MONEY_SELECT: string = CUSTOMER_NON_MONEY_COLUMNS.join(", ");
